// Inviting members to a game (src/office/invite.ts): 「邀请成员」 on the board-game panel and the 你画我猜 card opens the
// members picker (public/members.js: everyone online here, people and agents, with their status; one for a table,
// several for 你画我猜); someone's card when I walk past (greet.js) has 「邀请」 too. Where I am picks the game: in the
// 你画我猜 hut it's 你画我猜, at a board-game table (its panel open) that table. Each invite shows 等待回复… / 接受了，
// 正在过来 / 婉拒了 / 没回应 / 离开了 / 已入座. Invited myself: a card (members.js) 「X 邀请你下五子棋（棋桌 1）」 with
// 接受 (my otter walks to the table and sits, or into the hut; while I'm in a game it waits until that's over) / 拒绝.
import { icon, label, put } from '/icons.js';
import { t, has, onLang } from '/i18n.js';
/** How an invite of mine went: a catalog key per status (public/i18n.js). */
const STATUS = { pending: 'invite.st.pending', accepted: 'invite.st.accepted', declined: 'invite.st.declined', expired: 'invite.st.expired', left: 'invite.st.left', seated: 'invite.st.seated', noshow: 'invite.st.noshow' };
const status = (s) => (STATUS[s] ? t(STATUS[s]) : s);
const CSS = `.invrow{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-top:8px;font-size:12px}
.invrow>button{font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:3px 10px;background:#fff;cursor:pointer}
.invrow>button[hidden]{display:none}
.invst{flex-basis:100%;color:#555} .invst div{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.invst .accepted,.invst .seated{color:#15803d} .invst .declined,.invst .expired,.invst .left,.invst .noshow{color:#999}`;

/** @param o {send, me, peers, zoneAt, gomoku, toast, members (public/members.js), MAP, guest() (a 访客: accepts invites, sends none)} */
export function initInvite(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  /** My invites by id: {id, toName, game, table, status}. */
  const mine = new Map();
  const rows = [];

  /** The game where I am, or null. */
  function context() {
    const m = o.me(); if (!m) return null;
    if (o.zoneAt(m.x, m.y)?.id === 'game') return { game: 'pict', label: t('invite.game.pict') };
    const g = o.gomoku.current();
    if (g?.table && (!g.seats[0] || !g.seats[1])) return { game: 'gomoku', table: g.table, label: g.kindName ?? t('invite.game.gomoku') };
    return null;
  }
  function invite(p, ctx = context()) {
    if (!ctx) { o.toast(t('invite.noGame.card')); return; }
    o.send({ t: 'invite', to: p.id, game: ctx.game, ...(ctx.table && { table: ctx.table }) });
  }

  /** A row under a game's panel: the button, the agents to pick from, and my invites for this game. */
  function mount(host, game) {
    if (!host || host.querySelector('.invrow')) return;
    const row = document.createElement('div'); row.className = 'invrow';
    const b = document.createElement('button'); label(b, 'people', t('invite.members'));
    const stl = document.createElement('div'); stl.className = 'invst';
    row.append(b, stl); host.appendChild(row);
    b.onclick = () => pick();
    rows.push({ row, b, stl, game });
    paint();
  }
  /** Past `expires` with no word from the office (it may have missed telling me): 没回应. */
  function lapse() { const now = Date.now(); for (const i of mine.values()) if (i.status === 'pending' && i.expires && i.expires <= now) i.status = 'expired'; }
  function paint() {
    lapse();
    const ctx = context();
    for (const r of rows) {
      r.b.hidden = !ctx || ctx.game !== r.game; // e.g. both seats taken: nobody to invite to this table
      const mineHere = [...mine.values()].filter((i) => i.game === r.game).slice(-3);
      r.stl.replaceChildren(...mineHere.map((i) => { const d = document.createElement('div'); d.className = i.status; put(d, i.toKind === 'human' ? 'otter' : 'robot', t('invite.row', { name: i.toName, status: status(i.status) })); if (i.status === 'accepted' || i.status === 'seated') d.append(' ', icon('check')); return d; }));
    }
  }

  /** The members picker, for the game where I am: one person for a table (a seat is free), several for 你画我猜. */
  function pick() {
    const ctx = context();
    if (!ctx) { o.toast(t('invite.noGame.pick')); return; }
    const k = (x) => x.game + ':' + (x.table ?? '');
    o.members.pick({
      title: () => (ctx.table ? t('invite.pick.titleTable', { game: ctx.label, table: o.MAP.tables?.find((x) => x.id === ctx.table)?.name ?? ctx.table }) : t('invite.pick.title', { game: ctx.label })), icon: 'dice', max: ctx.game === 'gomoku' ? 1 : 5, // 5: the office's INVITE_MAX a minute
      alive: () => { const c = context(); return !!c && k(c) === k(ctx); }, closeOnSend: true,
      exclude: (p) => (ctx.game === 'pict' && o.zoneAt(p.x, p.y)?.id === 'game') || (ctx.game === 'gomoku' && o.gomoku.current()?.seats?.some((s) => s && s.id === (p.gid ?? p.sub))),
      state: (p) => {
        lapse();
        const last = [...mine.values()].filter((i) => i.to === p.id && i.game === ctx.game).at(-1);
        return last ? { text: status(last.status), cls: { accepted: 'ok', seated: 'ok', pending: 'wait' }[last.status] ?? 'off', lock: last.status === 'pending' } : null;
      },
      onSend: (ids) => { for (const id of ids) { const p = o.peers().get(id); if (p) invite(p, ctx); } },
    });
  }
  /** Walk to where an invite I accepted is: sit at the table (gomoku.js tableClick walks up and sits), or into the hut. */
  function go(m) {
    if (m.game === 'pict') return o.members.walkToZone('game', t('invite.hut'));
    const tb = o.MAP.tables?.find((x) => x.id === m.table);
    if (!tb || !o.gomoku.tableClick(tb.x, tb.y)) o.toast(t('invite.tableFull'));
  }

  // The panels are made by gomoku.js / games.js; hook a row under each once it exists.
  const tryMount = () => { mount(document.getElementById('gmpanel')?.querySelector('.gmbody') ?? document.getElementById('gmpanel'), 'gomoku'); mount(document.getElementById('globby'), 'pict'); };
  // a guest (访客) can't invite anyone (office.ts GUEST_DENIED): no 「邀请成员」 rows; it can still accept an invite
  if (!o.guest?.()) { tryMount(); setInterval(() => { tryMount(); paint(); }, 1000); }
  onLang(() => { for (const r of rows) label(r.b, 'people', t('invite.members')); paint(); });

  return {
    /** For the agent's profile card (greet.js): what its button says, and what it does. */
    forCard(p) {
      if (o.guest?.()) return null; // a guest: no 「邀请」 on anyone's card
      lapse();
      const ctx = context(), last = [...mine.values()].filter((i) => i.to === p.id).at(-1);
      if (last?.status === 'pending') return { icon: 'dice', label: t('invite.card.pending'), disabled: true };
      if (ctx?.game === 'pict' && o.zoneAt(p.x, p.y)?.id === 'game') return null; // already in the hut
      if (last?.status === 'accepted' && Date.now() - last.at < 60_000) return { icon: 'check', label: t('invite.card.accepted'), disabled: true };
      // away from a game: nothing (artin: the grey 「邀请」 was no use; at a table, the game itself asks people in)
      return ctx ? { icon: 'dice', label: t('invite.card.play', { game: ctx.label }), go: () => invite(p, ctx) } : null;
    },
    msg(m) {
      if (m.t === 'invite') { // invited myself (office/invite.ts, people too): the card
        const name = m.name || t('invite.otter'); // a table can be 五子棋 / 象棋 / 围棋 ...
        const text = m.game === 'pict' ? t('invite.got.pict', { name }) : m.tableName ? t('invite.got.boardTable', { name, table: m.tableName }) : t('invite.got.board', { name });
        const answer = (accept) => o.send({ t: 'invite_answer', id: m.id, accept });
        o.members.addCard({ id: 'g:' + m.id, icon: 'dice', text, quiet: !!m.quiet, expires: m.expires,
          accept: () => { answer(true); go(m); }, // no 「结束后再去」: the office counts 没过来 SEAT_WAIT_MS after 接受
          decline: () => answer(false) });
        return;
      }
      if (m.t === 'invite_gone') { o.members.dropCard('g:' + m.id); return; }
      if (m.t === 'invite_ok' || m.t === 'invite_status') {
        const was = mine.get(m.id);
        mine.set(m.id, { ...was, ...m, at: was?.at ?? Date.now() });
        const ic = m.toKind === 'human' ? 'people' : 'robot';
        if (m.t === 'invite_ok') o.toast(t('invite.sent', { name: m.toName }), null, ic);
        else if (m.status === 'accepted') o.toast(t('invite.accepted', { name: m.toName }), null, ic);
        else if (m.status === 'declined') o.toast(t('invite.declined', { name: m.toName }), null, ic);
        else if (m.status === 'seated') o.toast(t(m.game === 'pict' ? 'invite.seated.pict' : 'invite.seated.board', { name: m.toName }), null, 'check');
        else if (m.status === 'noshow') o.toast(t('invite.noshow', { name: m.toName }), null, ic);
        while (mine.size > 20) mine.delete(mine.keys().next().value);
      } else if (m.t === 'invite_err') {
        o.toast(m.code && has(`invite.err.${m.code}`) ? t(`invite.err.${m.code}`) : t('invite.failed')); // invite.err.<code>: the office's refusals (src/office/invite.ts)
      }
      paint();
    },
  };
}
